import { GridColDef } from "@mui/x-data-grid-pro";
import { GKClientDataGrid, Grid2, Typography } from "components";
import { ModelPortfolio } from "interfaces/OrderSimulation";
import { Controller, useFormContext } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import { extraColumnsField } from "utils/helpers";
import FilterPopover from "./FilterPopover";

interface Props {
  modelPortfolioList: ModelPortfolio[];
  modelPortfolioFetching: boolean;
  watchAllocationType: string;
}

const PortfolioSelectionTable = (props: Props) => {
  const { modelPortfolioList, modelPortfolioFetching, watchAllocationType } =
    props;

  const form = useFormContext();

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio (${modelPortfolioList.length})`,
        "clientName",
        250,
        1,
      ),
      sortable: false,
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioCode}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Portfolio Value (₹)", "portfolioValue", 160, 1),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Current Value (₹)", "currentAmount", 160, 1),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Available Cash (₹)", "availableCash", 160, 1),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField(
        "Available Cash %",
        "availableCashPercentage",
        140,
        1,
      ),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField(
        "Cash Equivalent (₹)",
        "availableCashEquivalent",
        160,
        1,
      ),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Total Cash (₹)", "totalCash", 120, 1),
      type: "number",
      sortable: false,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
  ];

  return (
    <Controller
      name="allocation.portfolios"
      control={form.control}
      render={({ field }) => {
        const selectedIds = field.value.map(
          (p: { portfolioId: number }) => p.portfolioId,
        );
        return (
          <GKClientDataGrid
            name="selectPortfolio"
            loading={modelPortfolioFetching}
            columns={columns}
            rows={modelPortfolioList || []}
            getRowId={(op) => op?.portfolioId}
            maxHeight={"calc(100vh - 401px)"}
            checkboxSelection
            keepNonExistentRowsSelected
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={(newSelectedIds) => {
              const prevSelected =
                form.getValues("allocation.portfolios") || [];

              const selectedRows = modelPortfolioList.filter((row) =>
                newSelectedIds.includes(row.portfolioId),
              );

              const updatedSelectedRows = selectedRows.map((row) => {
                const existing = prevSelected.find(
                  (p: any) => p.portfolioId === row.portfolioId,
                );
                return {
                  ...row,
                  id: row.portfolioId,
                  cash:
                    watchAllocationType === "rebalance" && row.availableCash > 0
                      ? row.availableCash
                      : existing?.cash || "",
                };
              });

              form.setValue("allocation.portfolios", updatedSelectedRows);
            }}
            headerComponent={
              <Typography
                fontSize={12}
                fontWeight={500}
                color=" #697586"
                display={"flex"}
                alignItems={"center"}
                gap={0.5}
              >
                Selected Portfolios:
                <Typography fontSize={12} fontWeight={600} color="#44484F">
                  {selectedIds.length || 0}
                </Typography>
              </Typography>
            }
            extraComponent={<FilterPopover />}
          />
        );
      }}
    />
  );
};

export default PortfolioSelectionTable;
